"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBasket = useBasket;
var _asyncToGenerator2 = _interopRequireDefault(require("@babel/runtime/helpers/asyncToGenerator"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = require("react");
var _useEinstein = require("./useEinstein");
var _contexts = require("../contexts");
var _useCustomer = require("../custom/hooks/useCustomer");
var _utils = require("../utils");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
function useBasket(opts = {}) {
  const {
    currency
  } = opts;
  const api = (0, _contexts.useCommerceAPI)();
  const customer = (0, _useCustomer.useCustomer)();
  const einstein = (0, _useEinstein.useEinstein)();
  const {
    basket,
    setBasket: _setBasket
  } = (0, _react.useContext)(_contexts.BasketContext);
  const setBasket = basketData => {
    _setBasket(_objectSpread({}, basketData));
  };
  const self = (0, _react.useMemo)(() => {
    return _objectSpread(_objectSpread({}, basket), {}, {
      // Check if a this represents a valid basket
      get loaded() {
        return basket && basket.basketId;
      },
      get itemCount() {
        var _basket$productItems;
        return (basket === null || basket === void 0 ? void 0 : (_basket$productItems = basket.productItems) === null || _basket$productItems === void 0 ? void 0 : _basket$productItems.length) || 0;
      },
      /** Items taking into account quantity of each */
      get itemAccumulatedCount() {
        var _basket$productItems2;
        return (basket === null || basket === void 0 ? void 0 : (_basket$productItems2 = basket.productItems) === null || _basket$productItems2 === void 0 ? void 0 : _basket$productItems2.reduce((prev, next) => prev + next.quantity, 0)) || 0;
      },
      /** Sum of all order-level discounts */
      get totalOrderDiscount() {
        var _basket$orderPriceAdj;
        return (basket === null || basket === void 0 ? void 0 : (_basket$orderPriceAdj = basket.orderPriceAdjustments) === null || _basket$orderPriceAdj === void 0 ? void 0 : _basket$orderPriceAdj.reduce((sum, item) => {
          return sum + item.price;
        }, 0)) || 0;
      },
      /**
       * Get an existing basket, if basket doesn't exist, create a new one.
       *
       * NOTE: This request is the only time we are using the ShopperCustomers API
       * to interact with a customer basket. All other calls are done through the
       * ShopperBaskets API, which in our case, uses OCAPI rather than commerce sdk.
       */
      getOrCreateBasket(opts = {}) {
        var _this = this;
        return (0, _asyncToGenerator2.default)(function* () {
          let basket;
          const {
            paymentError,
            orderNo,
            token
          } = opts;
          // @ts-ignore
          const customerBaskets = yield api.shopperCustomers.getCustomerBaskets({
            parameters: {
              customerId: customer.customerId
            }
          });

          // Throw if there was a problem getting the customer baskets
          if (!(0, _utils.isError)(customerBaskets)) {
            // We only support single baskets for now. Grab the first one.
            basket = Array.isArray(customerBaskets === null || customerBaskets === void 0 ? void 0 : customerBaskets.baskets) && customerBaskets.baskets[0];
          } else {
            throw new Error(customerBaskets);
          }
          if (paymentError === 'true' || paymentError === true) {
            if (basket) {
              try {
                // @ts-ignore
                yield api.shopperBaskets.deleteBasket({
                  parameters: {
                    basketId: basket.basketId
                  }
                });
              } catch (err) {
                // Ignore
              }
            }

            // @ts-ignore
            basket = yield api.shopperBaskets.createBasket({
              parameters: {
                reopenBasket: true,
                orderNo: orderNo,
                token: token
              }
            });
            if ((0, _utils.isError)(basket)) {
              throw new Error(basket);
            }
          }
          if (!basket) {
            // Back to using ShopperBaskets for all basket interaction.
            // @ts-ignore
            basket = yield api.shopperBaskets.createBasket({});

            // Throw if there was a problem creating the basket
            if ((0, _utils.isError)(basket)) {
              throw new Error(basket);
            }
          }

          // Update basket currency if it was created with the wrong one, this will also set the state.
          if (currency && basket.currency !== currency) {
            yield _this.updateBasketCurrency(currency, basket.basketId);
          } else {
            setBasket(basket);
          }
          return basket;
        })();
      },
      /**
       * Update the currency of the basket
       * @param currency - The currency code.
       * @param basketID - The id of the basket.
       * @returns {Promise<void>}
       */
      updateBasketCurrency(currency, basketId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const updateBasket = yield api.shopperBaskets.updateBasket({
            body: {
              currency
            },
            parameters: {
              basketId
            }
          });
          if ((0, _utils.isError)(updateBasket)) {
            throw new Error(updateBasket);
          } else {
            setBasket(updateBasket);
          }
        })();
      },
      /**
       * Add an item to the basket.
       *
       * @param {array} item
       * @param {string} item.productId - The id of the product.
       * @param {number} item.quantity - The quantity of the item.
       * @param {number} item.price - The price of the item.
       */
      addItemToBasket(item) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.addItemToBasket({
            body: item,
            parameters: {
              basketId: basket.basketId
            }
          });
          if (response.fault) {
            return response;
          } else {
            setBasket(response);
            item.map(eachItem => einstein.sendAddToCart(eachItem));
            return response;
          }
        })();
      },
      /**
       * Remove an item from the basket.
       *
       * @param {string} itemId - The id of the basket item.
       */
      removeItemFromBasket(itemId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.removeItemFromBasket({
            parameters: {
              basketId: basket.basketId,
              itemId: itemId
            }
          });
          if (response.fault) {
            throw new Error(response);
          } else {
            setBasket(response);
          }
        })();
      },
      /**
       * Update an item in the basket.
       *
       * @param {object} item
       * @param {string} item.productId - The id of the product.
       * @param {number} item.quantity - The quantity of the item.
       * @param {number} item.price - The price of the item.
       * @param {string} basketItemId - The id of the item.
       */
      updateItemInBasket(item, basketItemId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.updateItemInBasket({
            body: item,
            parameters: {
              basketId: basket.basketId,
              itemId: basketItemId
            }
          });
          if (response.fault) {
            return response;
          } else {
            setBasket(response);
            einstein.sendAddToCart(item);
            return response;
          }
        })();
      },
      /**
       * Get the product information for the given product ID.
       *
       * @param {string} productId - The id of the product.
       */
      getProductDetail(productId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperProducts.getProduct({
            parameters: {
              id: productId
            }
          });
          return response;
        })();
      },
      /**
       * Get the product availability for the given product ID.
       *
       * @param {string} productId - The id of the product.
       */
      getProductAvailability(productId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.ocapiProducts.getProductAvailability({
            parameters: {
              productId
            }
          });
          return response;
        })();
      },
      /**
       * Set the shipping address for the current basket.
       * @external Address
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
       */
      setShippingAddress(address, useAsBillingParam = false) {
        return (0, _asyncToGenerator2.default)(function* () {
          const useAsBilling = basket.billingAddress !== undefined && !basket.billingAddress || useAsBillingParam;
          let response = // @ts-ignore
          yield api.shopperBaskets.updateShippingAddressForShipment({
            body: address,
            parameters: {
              basketId: basket.basketId,
              shipmentId: 'me',
              useAsBilling: useAsBilling
            }
          });
          if (useAsBilling) {
            // @ts-ignore
            response = yield api.shopperBaskets.updateBillingAddressForBasket({
              body: address,
              parameters: {
                basketId: basket.basketId,
                shipmentId: 'me'
              }
            });
          }
          setBasket(response);
        })();
      },
      /**
       * Set the shipping method for the current basket.
       *
       * @param {string} id - The id of the shipping method.
       */
      setShippingMethod(id) {
        return (0, _asyncToGenerator2.default)(function* () {
          const response = // @ts-ignore
          yield api.shopperBaskets.updateShippingMethodForShipment({
            body: {
              id
            },
            parameters: {
              basketId: basket.basketId,
              shipmentId: 'me'
            }
          });
          setBasket(response);
        })();
      },
      /**
       * Update the shipment of the current basket
       * @param {object} body - The update attributes as object
       */
      updateShipment(body = {}) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.updateShipment({
            body: body,
            parameters: {
              basketId: basket.basketId,
              shipmentId: 'me'
            }
          });
          setBasket(response);
        })();
      },
      /**
       * Set the billing address for the current basket.
       * @external Address
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
       */
      setBillingAddress(address) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.updateBillingAddressForBasket({
            body: address,
            parameters: {
              basketId: basket.basketId,
              shipmentId: 'me'
            }
          });
          setBasket(response);
        })();
      },
      /**
       * Set the payment instrument for the current basket
       *
       * NOTE: API does allow adding multiple payment instruments to split payment. However,
       * we are currently only handling a single payment instrument.
       * Commerce API does not have an endpoint to edit a payment instrument, but OCAPI does.
       * We want to emulate Commerce API behavior (when using OCAPI) so we'll just remove the
       * existing payment and add the new one to simulate editing. We're making an assumption
       * that our basket will never have more than one payment instrument applied at any time.
       *
       * @external PaymentInstrument
       * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#basketpaymentinstrumentrequest
       */
      setPaymentInstrument(paymentInstrument) {
        return (0, _asyncToGenerator2.default)(function* () {
          var _basket$paymentInstru;
          // Keep reference to existing payment instrument id
          let existingPaymentInstrumentId = basket.paymentInstruments && ((_basket$paymentInstru = basket.paymentInstruments[0]) === null || _basket$paymentInstru === void 0 ? void 0 : _basket$paymentInstru.paymentInstrumentId);

          // Add the new payment instrument to basket
          // @ts-ignore
          let response = yield api.shopperBaskets.addPaymentInstrumentToBasket({
            body: paymentInstrument,
            parameters: {
              basketId: basket.basketId
            }
          });

          // TOOO: Handle possible error when adding payment instrument.
          // We won't attempt to remove the previous payment instrument in this case.

          // Remove the previous payment instrument if it existed
          if (existingPaymentInstrumentId) {
            // @ts-ignore
            response = yield api.shopperBaskets.removePaymentInstrumentFromBasket({
              parameters: {
                basketId: basket.basketId,
                paymentInstrumentId: existingPaymentInstrumentId
              }
            });
          }
          setBasket(response);
        })();
      },
      /**
       * Remove the payment instrument for the current basket
       */
      removePaymentInstrument() {
        return (0, _asyncToGenerator2.default)(function* () {
          var _basket$paymentInstru2;
          let paymentInstrumentId = basket.paymentInstruments && ((_basket$paymentInstru2 = basket.paymentInstruments[0]) === null || _basket$paymentInstru2 === void 0 ? void 0 : _basket$paymentInstru2.paymentInstrumentId);
          if (!paymentInstrumentId) {
            return;
          }
          const response = // @ts-ignore
          yield api.shopperBaskets.removePaymentInstrumentFromBasket({
            parameters: {
              basketId: basket.basketId,
              paymentInstrumentId: paymentInstrumentId
            }
          });
          setBasket(response);
        })();
      },
      /**
       * Update the customer information for the current basket.
       *
       * @param {object} info
       * @param {string} info.email - The email of the customer.
       */
      updateCustomerInfo(info) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.updateCustomerForBasket({
            body: info,
            parameters: {
              basketId: basket.basketId
            }
          });
          setBasket(response);
        })();
      },
      /**
       * Apply a coupon/promo code to the current basket
       * @param {string} code - The promo code to be applied
       */
      applyPromoCode(code) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.addCouponToBasket({
            body: {
              code
            },
            parameters: {
              basketId: basket.basketId
            }
          });
          if (response.fault) {
            throw new Error(response);
          }
          setBasket(response);
        })();
      },
      /**
       * Remove a coupon/promo code from the current basket
       * @param {string} couponIemId - The item id of the appied code
       */
      removePromoCode(couponItemId) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.removeCouponFromBasket({
            parameters: {
              basketId: basket.basketId,
              couponItemId
            }
          });
          if (response.fault) {
            throw new Error(response);
          }
          setBasket(response);
        })();
      },
      /**
       * Fetches and returns promo details for the given IDs
       * @param {Array<string>} ids - The promo ids to fetch
       * @returns {Object} - API response containing data
       */
      getPromotions(ids) {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperPromotions.getPromotions({
            parameters: {
              ids: ids.join(',')
            }
          });
          return response;
        })();
      },
      /**
       * Creates an order using the current basket.
       */
      createOrder() {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperOrders.createOrder({
            // Changed in 05.04.2024
            // _sfdc_customer_id removed due to a problem with AWS, HTTP/3 and headers starting with underline.
            // Fix is still not available in pwa-kit 3.5
            // We should keep an eye for changes in
            // https://github.com/SalesforceCommerceCloud/pwa-kit/blob/0edd6b2ea26b9c23502755b6ccc7316a538a4c7a/packages/template-retail-react-app/app/pages/checkout/index.jsx#L69

            // We send the SLAS usid via this header. This is required by ECOM to map
            // Einstein events sent via the API with the finishOrder event fired by ECOM
            // when an Order transitions from Created to New status.
            // Without this, various order conversion metrics will not appear on reports and dashboards
            // @ts-ignore
            // headers: { _sfdc_customer_id: api.auth.usid },
            body: {
              basketId: basket.basketId
            }
          });
          if (response.fault || response.title && response.type && response.detail) {
            throw new Error(response.title);
          }
          return response;
        })();
      },
      /**
       * Fetches the applicable shipping methods for the current basket
       * @returns {Object} - API response containing data
       */
      getShippingMethods() {
        // @ts-ignore
        return api.shopperBaskets.getShippingMethodsForShipment({
          parameters: {
            basketId: basket.basketId,
            shipmentId: 'me'
          }
        });
      },
      /**
       * Merge data from the previous shopper's basket into the current shopper's active basket
       * and delete the previous shopper's basket.
       */
      mergeBasket() {
        return (0, _asyncToGenerator2.default)(function* () {
          // @ts-ignore
          const response = yield api.shopperBaskets.mergeBasket({
            headers: {
              'Content-Type': 'application/json' // This is not required since the request has no body but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
            },
            parameters: {
              createDestinationBasket: true // If the current shopper has an active basket, this parameter is ignored.
            }
          });
          if (response.fault) {
            throw new Error(response);
          }

          // Commented out because Merge response can not be extended on BE, so it doesn't include necessary data, instead a separate request is made to get the actual basket
          // setBasket(response, 'merge')
          yield self.getOrCreateBasket();
        })();
      }
    });
  }, [customer, basket, setBasket]);
  return self;
}